import React, { useState, forwardRef, MouseEvent } from "react";

interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CustomSignatureCanvasProps {
  signatureURL: any;
  onSelectBox: (box: { x: number; y: number; width: number; height: number }) => void;
  onWidth: (width: number) => void;
  onHeight: (height: number) => void;
}

const CustomSignatureCanvas = forwardRef<HTMLCanvasElement, CustomSignatureCanvasProps>(
  ({ signatureURL, onSelectBox, onWidth, onHeight }, canvasRef) => {
    const [startPos, setStartPos] = useState<{ x: number; y: number } | null>(null);
    const [endPos, setEndPos] = useState<{ x: number; y: number } | null>(null);
    const [dragging, setDragging] = useState<boolean>(false);

    const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setStartPos({ x, y });
      setEndPos(null);
      setDragging(true);
    };

    const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
      if (!dragging) return;
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setEndPos({ x, y });
    };

    const handleMouseUp = () => {
      setDragging(false);
      if (startPos && endPos) {
        const x = Math.min(startPos.x, endPos.x);
        const y = Math.min(startPos.y, endPos.y);
        const width = Math.abs(startPos.x - endPos.x);
        const height = Math.abs(startPos.y - endPos.y);
        onSelectBox({ x, y, width, height });
      }
    };

    const renderOverlayBox = () => {
      if (!startPos || !endPos) return null;

      const x = Math.min(startPos.x, endPos.x);
      const y = Math.min(startPos.y, endPos.y);
      const width = Math.abs(startPos.x - endPos.x);
      const height = Math.abs(startPos.y - endPos.y);

      onWidth(width);
      onHeight(height);

      return (
        <div
          style={{
            position: "absolute",
            top: y,
            left: x,
            width,
            height,
            border: "2px dashed #007bff",
            backgroundColor: "rgba(0, 123, 255, 0.1)",
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {signatureURL && (
            <img
              src={signatureURL}
              alt="Signature"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                transform: "scale(1.2)",
                transformOrigin: "center",
              }}
            />
          )}
        </div>
      );
    };

    return (
      <div
        style={{
          position: "relative",
          width: "600px",
          height: "800px",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        <canvas
          ref={canvasRef}
          style={{
            border: "1px solid black",
            width: "600px",
            height: "800px",
            cursor: "crosshair",
          }}
        />
        {renderOverlayBox()}
      </div>
    );
  }
);

// ✅ Set display name to fix ESLint warning
CustomSignatureCanvas.displayName = 'CustomSignatureCanvas';

export default CustomSignatureCanvas;

